import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PlayCircle, Upload, ShieldCheck, Send, Trash2 } from "lucide-react";
import ReportDialog from "@/components/ReportDialog";

const resolve = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function Learn() {
  const { user } = useAuth();
  const [tab, setTab] = useState("long");
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", kind: "long", file: null, thumbnail_url: "" });
  const [isAdmin, setIsAdmin] = useState(false);

  const load = () => api.get("/videos", { params: { kind: tab } }).then((r) => setVideos(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    if (!user) return;
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => {});
  }, [user]);

  const adminDelete = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? This removes the video from Learn.`)) return;
    try {
      await api.delete(`/admin/video/${v.id}`);
      toast.success("Video deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submit = async () => {
    if (!user) return toast.error("Sign in first");
    if (!user.qualified) return toast.error("Only verified creators can post");
    if (!form.title || !form.file) return toast.error("Title and video file required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("kind", form.kind);
      fd.append("thumbnail_url", form.thumbnail_url);
      await api.post("/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video published");
      setOpen(false);
      setForm({ title: "", description: "", kind: "long", file: null, thumbnail_url: "" });
      setTab(form.kind);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-primary">// 02 · learn</div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">Signal from the field.</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Only verified scientists, academics and in-field workers can publish. Long-form deep dives on the left, field-clip shorts on the right.</p>
        </div>
        {user?.qualified && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="upload-video-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Upload video</Button></DialogTrigger>
            <DialogContent className="max-w-lg rounded-none">
              <DialogHeader><DialogTitle>Publish a video</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label>
                  <Input data-testid="v-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-none bg-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
                  <Textarea data-testid="v-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-none bg-input" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setForm({ ...form, kind: "long" })} className={`flex-1 border p-3 text-sm ${form.kind === "long" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="v-kind-long">Long-form</button>
                  <button onClick={() => setForm({ ...form, kind: "short" })} className={`flex-1 border p-3 text-sm ${form.kind === "short" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="v-kind-short">Short</button>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Video file (mp4/webm)</Label>
                  <Input data-testid="v-file" type="file" accept="video/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="mt-2 rounded-none bg-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Thumbnail URL (optional)</Label>
                  <Input value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className="mt-2 rounded-none bg-input" placeholder="https://…" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={busy} data-testid="v-submit" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy ? "Uploading…" : (<><Upload className="mr-2 h-4 w-4" />Publish</>)}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!user?.qualified && (
        <div className="mb-8 border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold">Publishing is gated to verified experts.</div>
            <div className="text-muted-foreground">Register as a Scientist or Content Creator with your credentials — you can earn per view once you publish.</div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none border border-border bg-transparent p-0">
          <TabsTrigger value="long" data-testid="learn-tab-long" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Long-form</TabsTrigger>
          <TabsTrigger value="short" data-testid="learn-tab-short" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Shorts</TabsTrigger>
        </TabsList>
        <TabsContent value="long"><Grid videos={videos} kind="long" isAdmin={isAdmin} onDelete={adminDelete} /></TabsContent>
        <TabsContent value="short"><Grid videos={videos} kind="short" isAdmin={isAdmin} onDelete={adminDelete} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Grid({ videos, kind, isAdmin, onDelete }) {
  if (videos.length === 0) {
    return <div className="mt-8 border border-dashed border-border p-16 text-center"><PlayCircle className="mx-auto h-10 w-10 text-muted-foreground" /><div className="mt-4 text-lg font-bold">No {kind === "long" ? "long-form" : "short"} videos yet</div><div className="text-sm text-muted-foreground mt-1">Verified experts will publish soon.</div></div>;
  }
  if (kind === "long") {
    return (
      <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {videos.map((v) => <LongCard key={v.id} v={v} isAdmin={isAdmin} onDelete={onDelete} />)}
      </div>
    );
  }
  return (
    <div className="mt-8 grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {videos.map((v) => <ShortCard key={v.id} v={v} isAdmin={isAdmin} onDelete={onDelete} />)}
    </div>
  );
}

function LongCard({ v, isAdmin, onDelete }) {
  const { user } = useAuth();
  const [play, setPlay] = useState(false);
  const ref = useRef(null);
  const start = () => { setPlay(true); api.post(`/videos/${v.id}/view`).catch(() => {}); setTimeout(() => ref.current?.play(), 50); };
  return (
    <div data-testid={`video-long-${v.id}`} className="border border-border hard-hover">
      <div className="relative aspect-video bg-muted">
        {play ? (
          <video ref={ref} src={resolve(v.video_url)} controls className="h-full w-full object-cover" />
        ) : (
          <button onClick={start} className="group relative h-full w-full">
            {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-secondary to-muted" />}
            <div className="absolute inset-0 flex items-center justify-center bg-background/40 group-hover:bg-background/20 transition-colors">
              <div className="border-2 border-primary p-4 bg-background/70"><PlayCircle className="h-8 w-8 text-primary" /></div>
            </div>
          </button>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold tracking-tight line-clamp-2">{v.title}</h3>
        <div className="mt-2 flex items-center justify-between text-xs">
          <div className="text-muted-foreground">
            {v.author_name} <span className="text-primary uppercase tracking-widest">· {v.author_role}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="mono text-muted-foreground">{v.views} views</span>
            {user && <ReportDialog targetType="video" targetId={v.id} compact />}
            {isAdmin && (
              <button
                data-testid={`admin-delete-video-${v.id}`}
                onClick={() => onDelete(v)}
                className="flex items-center gap-1 border border-destructive/60 px-2 py-1 text-[10px] uppercase tracking-widest text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            {user && <SendVideoButton video={v} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShortCard({ v, isAdmin, onDelete }) {
  const { user } = useAuth();
  const [play, setPlay] = useState(false);
  const ref = useRef(null);
  const start = () => { setPlay(true); api.post(`/videos/${v.id}/view`).catch(() => {}); setTimeout(() => ref.current?.play(), 50); };
  return (
    <div data-testid={`video-short-${v.id}`} className="border border-border hard-hover">
      <div className="relative aspect-[9/16] bg-muted">
        {play ? <video ref={ref} src={resolve(v.video_url)} controls className="h-full w-full object-cover" /> : (
          <button onClick={start} className="relative h-full w-full">
            {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-secondary to-muted" />}
            <div className="absolute inset-0 flex items-center justify-center bg-background/40 hover:bg-background/20">
              <PlayCircle className="h-10 w-10 text-primary" />
            </div>
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold line-clamp-2 flex-1">{v.title}</div>
          <div className="flex items-center gap-1">
            {user && <ReportDialog targetType="video" targetId={v.id} compact />}
            {isAdmin && (
              <button
                data-testid={`admin-delete-video-short-${v.id}`}
                onClick={() => onDelete(v)}
                className="flex items-center gap-1 border border-destructive/60 px-1 py-1 text-[10px] text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            {user && <SendVideoButton video={v} compact />}
          </div>
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{v.author_name}</div>
      </div>
    </div>
  );
}

function SendVideoButton({ video, compact }) {
  const [open, setOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) api.get("/friends").then((r) => setFriends(r.data.friends)).catch(() => {}); }, [open]);

  const send = async (to_id) => {
    setBusy(true);
    try {
      await api.post("/messages", { to_id, text: note, video_id: video.id });
      toast.success("Sent to friend");
      setOpen(false); setNote("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`share-${video.id}`} className="flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:border-primary hover:text-primary">
          <Send className="h-3 w-3" />{!compact && "Send"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle>Send to a friend</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground">Sharing: <span className="font-bold text-foreground">{video.title}</span></div>
        <Textarea data-testid="share-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a message…" className="mt-2 rounded-none bg-input" />
        <div className="mt-3 max-h-64 overflow-y-auto border border-border">
          {friends.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">You have no friends yet. <Link to="/friends" className="text-primary hover:underline">Add friends →</Link></div>
          ) : friends.map((f) => (
            <button key={f.id} data-testid={`share-to-${f.id}`} onClick={() => send(f.id)} disabled={busy} className="w-full flex items-center gap-2 p-3 text-left hover:bg-primary/5 border-b border-border last:border-b-0">
              <div className="flex h-7 w-7 items-center justify-center bg-primary/20 text-xs font-bold text-primary">{f.display_name?.[0]?.toUpperCase()}</div>
              <div className="text-sm font-bold">{f.display_name}</div>
              <Send className="ml-auto h-3 w-3 text-primary" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
